import produce, { Draft } from 'immer';
import { getType } from 'typesafe-actions';

import { CanvasAction } from '../common/actions';
import { GalleryPaneTypes } from '../GalleryPane/typings';

import {
    hideLoading,
    init,
    reset,
    resetAll,
    resetAudioFileId,
    restoreAudioFileId,
    setEditorData,
    setFile,
    setPreview,
    showErrors,
    sourceChange,
    uploadCancelSuccess,
    uploadSourceSelected,
    waitForFileLoaded
} from './actions';
import {
    fileEditorsWithResourceType,
    getAudioFileEditorId,
    getInitialFileEditorStatus,
    getPreviewResource,
    isFromGallery
} from './helpers';
import { FileEditorsState, FileEditorState, FileEditorStatus } from './typings';

export const getDefaultFileEditorState = (): FileEditorState => ({
    status: FileEditorStatus.NEW,
    fileId: null
});

export function fileEditors(state: FileEditorsState = {}, action: CanvasAction): FileEditorsState {
    return produce(state, (draft: Draft<FileEditorsState>) => {
        switch (action.type) {
            case getType(setPreview): {
                const { editorId, item, fileLoaded } = action;
                if (!draft[editorId]) {
                    draft[editorId] = getDefaultFileEditorState();
                }

                const preview = getPreviewResource(item);
                draft[editorId].preview = preview;

                if (fileLoaded) {
                    draft[editorId].savedPreview = preview;
                    draft[editorId].fromGallery = isFromGallery(item);
                }

                if (item.type === undefined) {
                    draft[editorId].cropSourceUrl = item.cropSourceUrl;
                    draft[editorId].url = item.url;
                }

                return;
            }

            case getType(init): {
                const { editorId, fileId, options } = action;

                if (!draft[editorId]) {
                    draft[editorId] = getDefaultFileEditorState();
                }

                draft[editorId].fileId = fileId;
                draft[editorId].options = options;
                draft[editorId].status = getInitialFileEditorStatus(fileId);

                const sameResourceEditors = fileEditorsWithResourceType(draft, options.resourceType, editorId);
                if (!sameResourceEditors.length) {
                    return;
                }

                sameResourceEditors.forEach(id => {
                    if (draft[id].hasRecentSource !== undefined) {
                        draft[editorId].hasRecentSource = draft[id].hasRecentSource;
                    }

                    if (draft[id].hasIdeaSource !== undefined) {
                        draft[editorId].hasIdeaSource = draft[id].hasIdeaSource;
                    }
                });

                return;
            }

            case getType(setFile): {
                const { editorId, item } = action;

                if (!draft[editorId]) {
                    draft[editorId] = getDefaultFileEditorState();
                }

                const preview = getPreviewResource(item);
                draft[editorId] = {
                    ...draft[editorId],
                    status: FileEditorStatus.EDIT,
                    fileId: item.id,
                    preview,
                    savedPreview: preview,
                    fromGallery: isFromGallery(item),
                    upload: false,
                    longUpload: false,
                    loadingFileId: undefined
                };

                if (item.type === undefined) {
                    draft[editorId].cropSourceUrl = item.cropSourceUrl;
                    draft[editorId].url = item.url;
                }

                return;
            }

            case getType(setEditorData): {
                const {
                    editingFileInfo: { editorId, preview, fileId, cropSourceUrl, origSrc }
                } = action;

                if (!draft[editorId]) {
                    draft[editorId] = getDefaultFileEditorState();
                }

                draft[editorId] = {
                    ...draft[editorId],
                    status: FileEditorStatus.EDIT,
                    fileId,
                    preview,
                    savedPreview: undefined,
                    fromGallery: false,
                    upload: false,
                    longUpload: false,
                    loadingFileId: undefined,
                    cropSourceUrl,
                    url: origSrc
                };

                return;
            }

            case getType(reset): {
                const { editorId } = action;

                draft[editorId] = {
                    fileId: null,
                    preview: undefined,
                    savedPreview: undefined,
                    loadingFileId: undefined,
                    cropSourceUrl: undefined,
                    url: undefined,
                    status: FileEditorStatus.NEW,
                    upload: false,
                    longUpload: false
                };

                return;
            }

            case getType(resetAudioFileId): {
                const editorId = getAudioFileEditorId(draft);

                if (!editorId) {
                    return;
                }

                if (draft[editorId].fileId === null) {
                    return;
                }

                draft[editorId].savedFileId = draft[editorId].fileId;
                draft[editorId].fileId = null;

                return;
            }

            case getType(restoreAudioFileId): {
                const editorId = getAudioFileEditorId(draft);

                if (!editorId) {
                    return;
                }

                if (draft[editorId].savedFileId === undefined) {
                    return;
                }

                draft[editorId].fileId = draft[editorId].savedFileId || null;
                draft[editorId].savedFileId = undefined;

                return;
            }

            case getType(sourceChange): {
                Object.keys(draft).forEach(editorId => {
                    const editor = draft[editorId];

                    if (editor.options && editor.options.resourceType === action.resource) {
                        if (action.hasSourceItems[GalleryPaneTypes.recent] !== undefined) {
                            editor.hasRecentSource = action.hasSourceItems[GalleryPaneTypes.recent];
                        }

                        if (action.hasSourceItems[GalleryPaneTypes.idea] !== undefined) {
                            editor.hasIdeaSource = action.hasSourceItems[GalleryPaneTypes.idea];
                        }
                    }
                });

                return;
            }

            case getType(waitForFileLoaded): {
                const { editorId, fileId } = action;

                draft[editorId].longUpload = true;
                draft[editorId].status = FileEditorStatus.CONVERTING;
                draft[editorId].loadingFileId = fileId;

                return;
            }

            case getType(uploadCancelSuccess): {
                const { editorId } = action;

                // возвращаем старое превью
                draft[editorId].preview = draft[editorId].savedPreview;
                draft[editorId].upload = false;
                draft[editorId].longUpload = false;
                draft[editorId].status = draft[editorId].fileId ? FileEditorStatus.EDIT : FileEditorStatus.NEW;
                draft[editorId].loadingFileId = undefined;

                return;
            }

            case getType(uploadSourceSelected): {
                const { editorId } = action;

                draft[editorId].upload = true;

                return;
            }

            case getType(hideLoading): {
                const { editorId } = action;

                draft[editorId].upload = false;

                return;
            }

            case getType(showErrors): {
                const { editorId } = action;

                draft[editorId].upload = false;
                draft[editorId].longUpload = false;

                return;
            }

            case getType(resetAll): {
                return {};
            }

            default:
                return;
        }
    });
}
